"use client";

import { useTranslation } from "@workspace/i18n";
import { buttonVariants } from "@workspace/ui-web/button";
import { Icons } from "@workspace/ui-web/icons";

import { pathsConfig } from "~/config/paths";
import { authClient } from "~/lib/auth/client";
import { SiteBrand } from "~/modules/common/site-brand";
import { TurboLink } from "~/modules/common/turbo-link";

import { MobileNavigation } from "./navigation/mobile-navigation";
import { Navigation } from "./navigation/navigation";

const links = [
  {
    label: "marketing:nav.product",
    href: pathsConfig.marketing.marineSurveySoftware,
    items: [
      {
        title: "marketing:nav.items.marineSurveySoftware.title",
        description: "marketing:nav.items.marineSurveySoftware.description",
        href: pathsConfig.marketing.marineSurveySoftware,
        icon: Icons.BookOpen,
      },
      {
        title: "marketing:nav.items.marineSurveyApp.title",
        description: "marketing:nav.items.marineSurveyApp.description",
        href: pathsConfig.marketing.marineSurveyApp,
        icon: Icons.BadgeCheck,
      },
      {
        title: "marketing:nav.items.howItWorks.title",
        description: "marketing:nav.items.howItWorks.description",
        href: pathsConfig.marketing.howItWorks,
        icon: Icons.ChartNoAxesGantt,
      },
      {
        title: "marketing:nav.items.features.title",
        description: "marketing:nav.items.features.description",
        href: pathsConfig.marketing.features,
        icon: Icons.Settings2,
      },
    ],
  },
  {
    label: "marketing:nav.pricing",
    href: `${pathsConfig.index}#pricing`,
  },
  {
    label: "marketing:nav.knowledgeCentre",
    href: pathsConfig.marketing.resources.index,
  },
  {
    label: "marketing:nav.faq",
    href: `${pathsConfig.index}#faq`,
  },
] as const;

interface HeaderProps {
  readonly enableAuth?: boolean;
}

const HeaderAuthButtons = () => {
  const { t } = useTranslation(["common", "auth"]);
  const { data } = authClient.useSession();

  if (data?.session) {
    return (
      <TurboLink href={pathsConfig.dashboard.user.index} className={buttonVariants()}>
        {t("common:dashboard")}
      </TurboLink>
    );
  }

  return (
    <>
      <TurboLink
        href={pathsConfig.auth.login}
        className={buttonVariants({ variant: "outline" })}
      >
        {t("auth:login.title")}
      </TurboLink>
      <TurboLink href={pathsConfig.auth.register} className={buttonVariants()}>
        {t("auth:register.title")}
      </TurboLink>
    </>
  );
};

const HeaderStaticButtons = () => {
  const { t } = useTranslation(["auth"]);

  return (
    <>
      <TurboLink
        href={pathsConfig.auth.login}
        className={buttonVariants({ variant: "outline" })}
      >
        {t("auth:login.title")}
      </TurboLink>
      <TurboLink href={pathsConfig.auth.register} className={buttonVariants()}>
        {t("auth:register.title")}
      </TurboLink>
    </>
  );
};

export const Header = ({ enableAuth = true }: HeaderProps) => {
  const { t } = useTranslation("common");
  return (
    <header className="bg-background/80 sticky inset-0 top-(--banner-height,0px) z-40 w-full py-3 backdrop-blur-sm">
      <div className="flex items-center justify-between px-6 pr-4 sm:container">
        <SiteBrand ariaLabel={t("home")} />

        <Navigation links={links} />

        <div className="flex items-center justify-center lg:gap-2">
          <div className="hidden items-center gap-2 lg:flex">
            {enableAuth ? <HeaderAuthButtons /> : <HeaderStaticButtons />}
          </div>

          <MobileNavigation links={links} enableAuth={enableAuth} />
        </div>
      </div>
    </header>
  );
};
